/* ============================================================================
 * PROJECT FILTER SUB-COMPONENTS
 * Sub-components for the ProjectFilterDropdown component
 * ============================================================================ */

import React from 'react';
import type { ThemeClasses } from '@/ui/theme/ThemeContext';
import { 
  ChevronDown, 
  Check, 
  Star, 
  Search, 
  Folder,
  Plus,
  X
} from 'lucide-react';
import { cn } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { getProjectSecondaryLabel } from '@/features/projects/utils/projectCompany';
import { ProjectInfo } from './ProjectFilterTypes';
import { getProjectColor } from './ProjectFilterUtils';

interface ProjectItemProps {
  project: ProjectInfo;
  isSelected: boolean;
  showColorIndicators: boolean;
  theme: ThemeClasses;
  onToggle: () => void;
}

/**
 * Individual project item component
 */
export const ProjectItem: React.FC<ProjectItemProps> = ({
  project,
  isSelected,
  showColorIndicators,
  theme,
  onToggle
}) => {
  const { t } = useSettings();
  const secondaryLabel = getProjectSecondaryLabel(project);

  return (
    <button
      type="button"
      data-testid={`project-filter-item-${project.projectId}`}
      aria-pressed={isSelected}
      className={cn(
        "flex w-full select-none items-center gap-2 px-3 py-2 text-left cursor-pointer transition-colors",
        theme.surfaceHover,
        theme.focusRing,
        isSelected && cn(theme.surfaceSelected, theme.selectionRing)
      )}
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
    {/* Checkbox */}
      <div className={cn(
        "w-4 h-4 rounded border flex items-center justify-center transition-colors",
        isSelected
          ? cn('border-transparent', theme.primaryAction)
          : cn(theme.bgPrimary, theme.borderPrimary, theme.textSecondary)
      )}>
        {isSelected && (
          <Check size={12} />
        )}
      </div>

    {/* Color indicator */}
    {showColorIndicators && (
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: getProjectColor(project.projectId, project.color) }}
      />
    )}

    {/* Project info */}
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span
          data-testid={`project-filter-item-title-${project.projectId}`}
          title={project.projectName}
          className={cn("text-sm font-medium truncate", theme.textPrimary)}
        >
          {project.projectName}
        </span>
        {project.role ? (
          <span
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              project.role === 'owner'
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                : project.role === 'editor'
                  ? 'bg-zinc-200 text-zinc-800 dark:bg-zinc-700 dark:text-zinc-100'
                  : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'
            )}
          >
            {t(project.role)}
          </span>
        ) : null}
        {project.isPinned && <Star size={12} className="text-yellow-500 fill-current" />}
      </div>
      {secondaryLabel && (
        <div
          data-testid={`project-filter-item-secondary-${project.projectId}`}
          title={secondaryLabel}
          className={cn("text-xs truncate", theme.textSecondary)}
        >
          {secondaryLabel}
        </div>
      )}
    </div>
    </button>
  );
};

interface ProjectSectionProps {
  sectionId: string;
  title: string;
  projects: ProjectInfo[];
  icon: React.ReactNode;
  isExpanded: boolean;
  selectedProjects: string[];
  showColorIndicators: boolean;
  theme: ThemeClasses;
  onToggleSection: () => void;
  onToggleProject: (projectId: string) => void;
}

/**
 * Project section component
 */
export const ProjectSection: React.FC<ProjectSectionProps> = ({
  sectionId,
  title,
  projects,
  icon,
  isExpanded,
  selectedProjects,
  showColorIndicators,
  theme,
  onToggleSection,
  onToggleProject
}) => {
  if (projects.length === 0) return null;

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={`project-filter-section-${sectionId}`}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-left cursor-pointer",
          "hover:bg-gray-50 dark:hover:bg-gray-700",
          theme.textSecondary,
          theme.focusRing
        )}
        onClick={onToggleSection}
      >
        <ChevronDown 
          size={14} 
          className={cn("transition-transform", !isExpanded && "-rotate-90")}
        />
        {icon}
        <span data-testid={`project-filter-section-heading-${sectionId}`} className="text-xs font-medium uppercase tracking-wider">
          {title} ({projects.length})
        </span>
      </button>
      {isExpanded && (
        <div id={`project-filter-section-${sectionId}`}>
          {projects.map(project => (
            <ProjectItem
              key={project.projectId}
              project={project}
              isSelected={selectedProjects.includes(project.projectId)}
              showColorIndicators={showColorIndicators}
              theme={theme}
              onToggle={() => onToggleProject(project.projectId)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface SearchHeaderProps {
  searchQuery: string;
  theme: ThemeClasses;
  searchInputRef: React.RefObject<HTMLInputElement>;
  onSearchChange: (value: string) => void;
  onSelectAll: () => void;
  onCreateProject?: () => void;
  selectAllText: string;
  showCreateButton: boolean;
}

/**
 * Dropdown header with search
 */
export const SearchHeader: React.FC<SearchHeaderProps> = ({
  searchQuery,
  theme,
  searchInputRef,
  onSearchChange,
  onSelectAll,
  onCreateProject,
  selectAllText,
  showCreateButton
}) => {
  const { t } = useSettings();

  return (
    <div data-testid="project-filter-header" className="p-3 border-b border-gray-200 dark:border-gray-700">
    <div className="flex items-center gap-2 mb-2">
      <Search size={14} className={theme.textSecondary} />
      <input
        data-testid="project-filter-search-input"
        ref={searchInputRef}
        type="text"
        placeholder={t('Search projects...')}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className={cn(
          "flex-1 rounded border bg-transparent px-2 py-1 text-sm focus:outline-none focus:border-zinc-300 dark:focus:border-zinc-600/70 focus:bg-white/70 dark:focus:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-zinc-400/70",
          theme.borderPrimary,
          theme.textPrimary
        )}
      />
      {searchQuery && (
        <button
          type="button"
          data-testid="project-filter-clear-search-button"
          aria-label={t('Clear search')}
          onClick={() => onSearchChange('')}
          className={cn("p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700")}
        >
          <X size={14} />
        </button>
      )}
    </div>

    {/* Quick actions */}
    <div className="flex items-center justify-between gap-2 min-h-7">
      <button
        type="button"
        onClick={onSelectAll}
        data-testid="project-filter-select-all-button"
        className={cn(
          "text-xs px-2 py-1 rounded min-w-[84px] text-center",
          "hover:bg-gray-100 dark:hover:bg-gray-700",
          theme.textSecondary
        )}
      >
        {selectAllText}
      </button>
      {showCreateButton && onCreateProject && (
        <button
          type="button"
          onClick={onCreateProject}
          data-testid="project-filter-create-button"
          className={cn(
            "inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[12px] whitespace-nowrap transition-colors",
            theme.borderPrimary,
            theme.textPrimary,
            theme.surfaceHover,
            theme.focusRing
          )}
        >
          <Plus size={12} />
          {t('New Project')}
        </button>
      )}
    </div>
  </div>
  );
};

interface SelectionFooterProps {
  selectedCount: number;
  theme: ThemeClasses;
  onClearSelection: () => void;
}

/**
 * Footer showing selection count
 */
export const SelectionFooter: React.FC<SelectionFooterProps> = ({
  selectedCount,
  theme,
  onClearSelection
}) => {
  const { t } = useSettings();

  return (
    <div className={cn(
    "project-filter-footer",
    "px-3 py-2 border-t border-gray-200 dark:border-gray-700 min-h-10",
    "text-xs",
    theme.textSecondary,
    selectedCount === 0 && "invisible"
  )}>
    <div data-testid="project-filter-footer" className="flex items-center justify-between">
      <span data-testid="project-filter-selection-count">
        {selectedCount === 1
          ? t('{{count}} project selected', { count: selectedCount })
          : t('{{count}} projects selected', { count: selectedCount })}
      </span>
      <button
        type="button"
        onClick={onClearSelection}
        data-testid="project-filter-clear-selection-button"
        className="text-red-500 hover:underline"
      >
        {t('Clear selection')}
      </button>
    </div>
  </div>
  );
};

interface EmptyStateProps {
  searchQuery: string;
  hasProjects: boolean;
  theme: ThemeClasses;
  onCreateProject?: () => void;
  showCreateButton?: boolean;
}

/**
 * Empty state component
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  searchQuery,
  hasProjects,
  theme,
  onCreateProject,
  showCreateButton = false,
}) => {
  const { t } = useSettings();

  if (searchQuery && hasProjects) {
    return (
      <div className="p-8 text-center">
        <p data-testid="project-filter-empty-search-message" className={cn("text-sm", theme.textSecondary)}>
          {t('No projects match "{{query}}"', { query: searchQuery })}
        </p>
      </div>
    );
  }

  if (!hasProjects) {
    return (
      <div className="p-8 text-center">
        <Folder size={32} className={cn("mx-auto mb-2", theme.textSecondary)} />
        <p data-testid="project-filter-empty-projects-message" className={cn("text-sm", theme.textSecondary)}>{t('No projects available')}</p>
        {showCreateButton && onCreateProject && (
          <button
            onClick={onCreateProject}
            data-testid="project-filter-empty-create-button"
            className={cn("mt-2 text-sm hover:underline", theme.textPrimary)}
          >
            {t('Create your first project')}
          </button>
        )}
      </div>
    );
  }

  return null;
};
